/**
 * Post-onboarding progress card.
 * Shows profile completion percentage with links to remaining steps.
 * Disappears when profile is "complete enough" (all key fields filled).
 */

import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { GlassCard } from '@/components/ui/layout/card';
import { CardContent } from '@/components/ui/layout/card';
import { cn } from '@/lib/utils';
import { CheckCircle2, Circle, ChevronRight } from 'lucide-react';

interface ProfileField {
  key: string;
  label: string;
  complete: boolean;
  /** Route to navigate to for completing this step */
  to: string;
  /** Coaching impact explanation shown for incomplete items */
  impact: string;
}

interface OnboardingProgressCardProps {
  /** Whether a device is connected */
  hasDevice: boolean;
  /** Whether the user has injury info set (not "none" default from quick onboard) */
  hasInjuryInfo: boolean;
  /** Whether experience level has been refined beyond "beginner" default */
  hasExperience: boolean;
  /** Whether the user has a race target set */
  hasRaceTarget: boolean;
}

export function OnboardingProgressCard({
  hasDevice,
  hasInjuryInfo,
  hasExperience,
  hasRaceTarget,
}: OnboardingProgressCardProps) {
  const { t } = useTranslation();

  const fields: ProfileField[] = [
    {
      key: 'device',
      label: t('dashboard.progress.device', { defaultValue: 'Connect a device' }),
      complete: hasDevice,
      to: '/profile#integrations',
      impact: t('dashboard.progress.impact.device', {
        defaultValue: 'Enables automatic activity sync and real-time load tracking',
      }),
    },
    {
      key: 'experience',
      label: t('dashboard.progress.experience', { defaultValue: 'Set experience level' }),
      complete: hasExperience,
      to: '/profile#experience',
      impact: t('dashboard.progress.impact.experience', {
        defaultValue: 'Coach tailors session difficulty and volume to your fitness history',
      }),
    },
    {
      key: 'injuries',
      label: t('dashboard.progress.injuries', { defaultValue: 'Add injury info' }),
      complete: hasInjuryInfo,
      to: '/profile#injuries',
      impact: t('dashboard.progress.impact.injuries', {
        defaultValue: 'Coach avoids movements that aggravate your specific issues',
      }),
    },
    {
      key: 'race',
      label: t('dashboard.progress.race', { defaultValue: 'Set race target' }),
      complete: hasRaceTarget,
      to: '/profile#race-target',
      impact: t('dashboard.progress.impact.race', {
        defaultValue: 'Unlocks periodized training blocks and race-specific taper plans',
      }),
    },
  ];

  const completedCount = fields.filter(f => f.complete).length;
  const totalCount = fields.length;
  const percentage = Math.round((completedCount / totalCount) * 100);

  // Don't show if all done
  if (completedCount === totalCount) return null;

  return (
    <GlassCard className="border-white/[0.06]">
      <CardContent className="p-4">
        <div className="flex items-center justify-between mb-3">
          <span className="font-mono text-[10px] uppercase tracking-wider text-muted-foreground">
            {t('dashboard.progress.title', { defaultValue: 'Profile Setup' })}
          </span>
          <span className="font-mono text-[10px] uppercase tracking-wider text-[#00FFC8]/80">
            {percentage}%
          </span>
        </div>

        {/* Progress bar */}
        <div className="h-1 bg-white/[0.06] rounded-full mb-3 overflow-hidden">
          <div
            className="h-full bg-[#00FFC8] rounded-full transition-all duration-500"
            style={{ width: `${percentage}%` }}
          />
        </div>

        {/* Checklist */}
        <div className="space-y-1.5">
          {fields.map(field => {
            const content = (
              <div className={cn(
                'group',
                !field.complete && 'rounded-md px-1.5 py-1.5 -mx-1.5 hover:bg-white/[0.04] transition-colors cursor-pointer'
              )}>
                <div className="flex items-center gap-2">
                  {field.complete ? (
                    <CheckCircle2 className="h-3.5 w-3.5 text-[#00FFC8] shrink-0" />
                  ) : (
                    <Circle className="h-3.5 w-3.5 text-white/20 shrink-0" />
                  )}
                  <span className={cn(
                    'text-xs flex-1',
                    field.complete ? 'text-muted-foreground line-through' : 'text-foreground'
                  )}>
                    {field.label}
                  </span>
                  {!field.complete && (
                    <ChevronRight className="h-3 w-3 text-white/20 group-hover:text-[#00FFC8] transition-colors shrink-0" />
                  )}
                </div>
                {!field.complete && (
                  <p className="text-[10px] text-muted-foreground/60 mt-0.5 ml-[22px]">
                    {field.impact}
                  </p>
                )}
              </div>
            );

            return field.complete ? (
              <div key={field.key}>{content}</div>
            ) : (
              <Link key={field.key} to={field.to}>{content}</Link>
            );
          })}
        </div>

        <p className="text-[10px] text-muted-foreground/60 mt-3 font-mono">
          {t('dashboard.progress.hint', { defaultValue: 'Tap an item to go there, or chat with the coach' })}
        </p>
      </CardContent>
    </GlassCard>
  );
}
